<template>

  <v-container fluid>

    <!-- (data loading spinner) -->
    <v-container class="d-flex flex-column align-center justify-center" v-if="isDataLoading">
      <v-progress-circular indeterminate :size="50" style="margin-bottom:30px" color="primary" />
    </v-container>

    <!-- Delivery Route Map -->
    <v-row v-else>
      <v-col sm="12" class="pa-0 ma-0">
        <map-viewer
            v-if="routeLayer"
            :settings="mapSettings"
            :deliverySteps="deliverySteps"
            :routeLayer="routeLayer" />
      </v-col>
    </v-row>

  </v-container>

</template>

<script>
/* global mapboxgl */
import MapViewer from '@/components/map/MapViewer'

export default {
  name: 'RouteMap',

  components: {
    MapViewer
  },

  props: {
    route: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      isDataLoading: false,
      activeError: null,
      hq: null,
      distributions: [],
      bounds: [],
      deliverySteps: [],
      routeLayer: null
    }
  },

  computed: {
    // The orders in the route
    orders () {
      return this.route.orders || []
    },
    routeInfo () {
      return this.route?.route_info || {}
    },
    // The parking lot (start/end of route)
    vehicleLot () {
      return this.route?.vehicle_lot || {}
    },
    // Map settings
    mapSettings () {
      const coords = this.bounds
      const bounds = coords.reduce((bounds, coord) => {
        return bounds.extend(coord)
      }, new mapboxgl.LngLatBounds(coords[0], coords[0]))

      return {
        minZoom: 6,
        maxZoom: 15,
        zoom: 9,
        bounds,
        fitBoundsOptions: { padding: { top: 120, bottom: 120, left: 120, right: 120 } }
      }
    }
  },

  async beforeMount () {
    this.isDataLoading = true

    // Build "hq" data
    const lon = this.$root.utils.get(this.vehicleLot, 'coords.longitude', null)
    const lat = this.$root.utils.get(this.vehicleLot, 'coords.latitude', null)
    this.hq = {
      coordinates: (lon && lat) ? [lon, lat] : null,
      properties: {
        name: this.vehicleLot.name,
        alias: this.vehicleLot.id,
        type: 'vehicle-lot',
        route: this.route
      }
    }

    // Build delivery steps for map and load route
    this.buildDeliverySteps()
    await this.loadSavedRouteLayer()

    this.isDataLoading = false
  },

  methods: {
    async loadSavedRouteLayer () {
      const accessUrl = await this.$root.fetchRouteAsset(this.route.id, 'map_layer_route')
      try {
        const res = await fetch(accessUrl)
        const layerData = await res.json()
        this.routeLayer = layerData
      } catch (err) {}
    },

    // Build delivery steps and bounds (for map)
    async buildDeliverySteps () {
      const deliverySteps = []

      // Add vehicle lot
      if (this.hq) {
        if (this.hq.coordinates) this.bounds.push(this.hq.coordinates) // add to bounds
        deliverySteps.push(this.hq)
      }

      // Add route changes
      if (this.routeInfo.route_changes && this.routeInfo.route_changes.length > 0) {
        for (const routeChange of this.routeInfo.route_changes) {
          if (routeChange?.loc?.coords && routeChange.loc.coords.longitude && routeChange.loc.coords.latitude) {
            const coords = [
              this.$root.utils.truncateDecimals(routeChange.loc.coords.longitude, 5),
              this.$root.utils.truncateDecimals(routeChange.loc.coords.latitude, 5)
            ]

            // Build map marker and add to bounds
            this.bounds.push(coords)
            deliverySteps.push({
              coordinates: coords,
              properties: {
                ...routeChange,
                // step_index: routeChange.route_step_index,
                type: 'route-change',
                name: 'Route Change'
              }
            })
          }
        }
      } // end-if (this.routeInfo.route_changes && this.routeInfo.route_changes.length > 0) {

      // Iterate all generated steps and build data for map display
      for (const [index, step] of this.routeInfo.steps.entries()) {
        // Handle re-route step
        if (step.type === 're-route') {
          if (step.location_coords && step.location_coords.longitude && step.location_coords.latitude) {
            const coords = [
              this.$root.utils.truncateDecimals(step.location_coords.longitude, 5),
              this.$root.utils.truncateDecimals(step.location_coords.latitude, 5)
            ]

            // Build map marker and add to bounds
            this.bounds.push(coords)
            deliverySteps.push({
              coordinates: coords,
              properties: {
                step_index: index + 1,
                type: step.type,
                name: step.location_name
              }
            })
          }
        } else {
          // Access location info from one of the orders
          const orders = step.order_ids.map(id => this.orders.filter(order => order.id === id)[0])
          if (orders && orders.length > 0) {
            const order = orders[0] // we only need one order to determine location info, since globbed

            // Determine location and parse coordinates
            const location = (step.type === 'pickup') ? order.shipper : order.receiver
            const locationType = (step.type === 'pickup') ? 'pickup-location' : 'dropoff-location'
            if (location && location.coords && location.coords.longitude && location.coords.latitude) {
              const coords = [
                this.$root.utils.truncateDecimals(location.coords.longitude, 5),
                this.$root.utils.truncateDecimals(location.coords.latitude, 5)
              ]

              // Build map marker and add to bounds
              this.bounds.push(coords)
              deliverySteps.push({
                coordinates: coords,
                properties: {
                  step_index: index + 1,
                  type: locationType,
                  name: location.name,
                  orders
                }
              })
            } // end-if (location && location.coords && location.coords.longitude && location.coords.latitude)
          } // end-if (orders && orders.length > 0)
        }
      } // end-for (this.routeInfo.steps)

      this.deliverySteps = deliverySteps
    }
  }
}
</script>

<style scoped>
  .map-area {
    color: #525252;
    text-align: center;
    border: 2px dashed #525252;
    border-radius: 4px;
    height: 80px;
    width: 100%;
  }
</style>
